import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { auth } from "../firebase/firebase";
import { setUser, logout } from "../store/slices/session/sessionSlice";

// Firebase ID tokens are valid for 1 hour by default
const TOKEN_EXPIRY_MS = 60 * 60 * 1000; // 1 hour in milliseconds
const REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // Refresh every 30 minutes
const IMMEDIATE_REFRESH_DELAY = 1000; // 1 second delay for immediate refresh

export const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTimeRef = useRef<number>(Date.now());

  // Use refs to break circular dependencies between functions
  const scheduleTokenRefreshRef = useRef<() => void>();
  const refreshTokenRef = useRef<() => Promise<void>>();

  // Schedule the next token refresh
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule refresh for 5 minutes before token expires
    const refreshTime = TOKEN_EXPIRY_MS - REFRESH_BUFFER_MS;
    console.log(
      `Scheduling next token refresh in ${refreshTime / 60000} minutes`
    );

    refreshTimeoutRef.current = setTimeout(() => {
      if (refreshTokenRef.current) {
        refreshTokenRef.current();
      }
    }, refreshTime);
  }, []);

  // Store in ref to avoid dependency cycles
  scheduleTokenRefreshRef.current = scheduleTokenRefresh;

  // Function to refresh the token and update Redux store
  const refreshToken = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn("No user found when attempting to refresh token");
        return;
      }

      console.log("Proactively refreshing Firebase token");
      const newToken = await user.getIdToken(true);
      lastRefreshTimeRef.current = Date.now();

      // Update Redux store with new token
      dispatch(
        setUser({
          uid: user.uid,
          email: user.email || "",
          token: newToken,
          creationTime: user.metadata.creationTime || "",
          lastSignInTime: user.metadata.lastSignInTime || "",
        })
      );

      // Schedule next refresh
      if (scheduleTokenRefreshRef.current) {
        scheduleTokenRefreshRef.current();
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      // If token refresh fails, log the user out
      dispatch(logout());
    }
  }, [dispatch]);

  // Store in ref to avoid dependency cycles
  refreshTokenRef.current = refreshToken;

  // Set up interval-based token refresh
  const setupIntervalRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    console.log(
      `Setting up interval token refresh every ${
        REFRESH_INTERVAL_MS / 60000
      } minutes`
    );

    // Initial refresh
    refreshToken();

    // Then set up interval
    refreshIntervalRef.current = setInterval(() => {
      refreshToken();
    }, REFRESH_INTERVAL_MS);
  }, [refreshToken]);

  // Check if token needs refresh based on time since last refresh
  const checkAndRefreshToken = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

    // If it's been more than 30 minutes since last refresh, refresh immediately
    if (timeSinceLastRefresh > REFRESH_INTERVAL_MS) {
      console.log(
        `Token might be stale (${
          timeSinceLastRefresh / 60000
        } minutes since last refresh). Refreshing now.`
      );
      // Add a small delay to ensure this doesn't block UI rendering
      setTimeout(() => refreshToken(), IMMEDIATE_REFRESH_DELAY);
    }
  }, [refreshToken]);

  // Handle visibility change to refresh token when app becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("App became visible, checking token freshness");
        checkAndRefreshToken();
      }
    };

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkAndRefreshToken]);

  // Also refresh token on page load/reload
  useEffect(() => {
    // Check if token needs refresh on initial load
    checkAndRefreshToken();
  }, [checkAndRefreshToken]);

  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          // Get a fresh token when user signs in
          const token = await user.getIdToken();

          // Update Redux store
          dispatch(
            setUser({
              uid: user.uid,
              email: user.email || "",
              token,
              creationTime: user.metadata.creationTime || "",
              lastSignInTime: user.metadata.lastSignInTime || "",
            })
          );

          // Set up both refresh mechanisms
          scheduleTokenRefresh();
          setupIntervalRefresh();
        } catch (error) {
          console.error("Error getting token on auth state change:", error);
        }
      }
    });

    // Clean up on unmount
    return () => {
      unsubscribe();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return the manual refresh function in case it's needed elsewhere
  return { refreshToken };
};
